import { Copy, TrashSimple } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateShareGrant, useRevokeShareGrant, useShareGrants } from "~/api/queries";
import type { GrantLinkType } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatDateTime } from "~/foundation/utils/time";

/**
 * W9 §A.3.2 — grant management. `share-grants.*` has zero frontend callers anywhere in kms
 * (grants could only be created or revoked by an API client) — this is the smallest honest
 * surface slate adds. Mounted from `DocumentHistoryScene` rather than the shell's share
 * dialog: `slate/src/shell/DialogHost.tsx` is out of this builder's scope, and document
 * lifecycle (history, merge, sharing) already lives on this scene.
 */
function shareUrl(urlId: string): string {
  // §E-9: the server's own `url` field reflects the raw Host header, not the alternate-host
  // allowlist — slate builds the link itself, which is also correct on every alias host.
  return `${window.location.origin}/p/${urlId}`;
}

const DEFAULT_EXPIRY_DAYS = 30;

export function ShareGrantsPanel({
  documentId,
  canShare,
}: {
  documentId: string;
  canShare: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { data: grants, isLoading } = useShareGrants(documentId, canShare);
  const create = useCreateShareGrant();
  const revoke = useRevokeShareGrant(documentId);

  const [linkType, setLinkType] = useState<GrantLinkType>("View");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [password, setPassword] = useState("");

  if (!canShare) {
    return null;
  }

  const submit = () => {
    const expiresAt = new Date(Date.now() + DEFAULT_EXPIRY_DAYS * 86_400_000).toISOString();
    create.mutate(
      {
        documentId,
        linkType: linkType.toLowerCase() as "view" | "comment" | "edit",
        ...(recipientEmail.trim() ? { recipientEmail: recipientEmail.trim() } : {}),
        ...(password.trim() ? { password: password.trim() } : {}),
        expiresAt,
      },
      {
        onSuccess: () => {
          if (recipientEmail.trim()) {
            // A write the person did not explicitly ask for is worth one sentence (§A.3.2).
            toast.info(t("We'll remember this recipient"));
          }
          setRecipientEmail("");
          setPassword("");
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-divider p-4">
      <Text as="h3" variant="small" weight="medium">
        {t("Share links")}
      </Text>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : grants && grants.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {grants.map((grant) => (
            <li key={grant.id} className="flex items-center gap-2 text-small">
              <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-text-tertiary text-xs">
                {grant.linkType}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-text-secondary">
                {shareUrl(grant.urlId)}
              </span>
              {grant.hasPassword ? (
                <span className="text-text-tertiary text-xs">{t("Password")}</span>
              ) : null}
              {grant.expiresAt ? (
                <span className="text-text-tertiary text-xs">
                  {t("Expires {{date}}", { date: formatDateTime(grant.expiresAt, i18n.language) })}
                </span>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                icon={Copy}
                aria-label={t("Copy link")}
                onClick={() => {
                  void navigator.clipboard.writeText(shareUrl(grant.urlId));
                  toast.success(t("Link copied"));
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                icon={TrashSimple}
                aria-label={t("Revoke")}
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(grant.id)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <Text variant="small">{t("Nothing is shared publicly")}</Text>
      )}

      <div className="flex flex-wrap items-end gap-2 border-t border-divider pt-3">
        <label className="flex flex-col gap-1">
          <span className="text-small text-text-secondary">{t("Access")}</span>
          <select
            value={linkType}
            onChange={(e) => setLinkType(e.target.value as GrantLinkType)}
            className="h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text"
          >
            <option value="View">{t("View")}</option>
            <option value="Comment">{t("Comment")}</option>
            <option value="Edit">{t("Edit")}</option>
          </select>
        </label>
        <Input
          label={t("Recipient email (optional)")}
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
        />
        <Input
          label={t("Password (optional)")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button variant="primary" size="sm" disabled={create.isPending} onClick={submit}>
          {t("Create link")}
        </Button>
      </div>
      <Text variant="small">{t("Links expire after 30 days by default.")}</Text>
    </div>
  );
}
